import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, BookOpen, Calendar, User, ChevronRight, Tag, Activity, AlertTriangle, Heart } from '../lib/icons';
import { useBlogTopicTags, useBlogPosts } from '../hooks/useBlog';
import { createUniqueSlug } from '../utils/slugUtils';
import BlogCard from './BlogCard';

interface BlogTopicTagsDisplayProps {
  blogPostId?: string;
  topicId?: string;
  showAsRelatedArticles?: boolean;
  className?: string;
}

const BlogTopicTagsDisplay: React.FC<BlogTopicTagsDisplayProps> = ({ 
  blogPostId,
  topicId,
  showAsRelatedArticles = false,
  className = "" 
}) => {
  const { blogTopicTags, loading } = useBlogTopicTags(blogPostId);
  const { blogPosts, loading: blogLoading } = useBlogPosts();
  const navigate = useNavigate();

  // Filter out inactive topics from blog topic tags
  const activeTopicTags = blogTopicTags.filter(blogTopicTag => 
    blogTopicTag.topic && blogTopicTag.topic.is_active
  );

  // If showing as related articles, find blog posts that reference this topic
  const relatedBlogPosts = React.useMemo(() => {
    if (!showAsRelatedArticles || !topicId || blogLoading) return [];
    
    // Find blog posts that have this topic as a tag
    return blogPosts.filter(post => {
      if (!post.blogTopicTags) return false;
      return post.blogTopicTags.some(tag => tag.topic_id === topicId);
    });
  }, [showAsRelatedArticles, topicId, blogPosts, blogLoading]);

  const getTopicColor = (topic: any) => {
    const topicType = topic.topic_type || 'consultation';
    switch (topicType) {
      case 'disease':
        return 'bg-red-100 text-red-800 border-red-200 hover:bg-red-200';
      case 'symptom':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200 hover:bg-yellow-200';
      case 'consultation':
      default:
        return 'bg-green-100 text-green-800 border-green-200 hover:bg-green-200';
    }
  };

  const getTopicIcon = (topic: any) => {
    const topicType = topic.topic_type || 'consultation';
    switch (topicType) {
      case 'disease':
      case 'symptom':
        return AlertTriangle;
      case 'consultation':
      default:
        return Heart;
    }
  };

  const getTopicTypeLabel = (topic: any) => {
    const topicType = topic.topic_type || 'consultation';
    switch (topicType) {
      case 'disease':
        return 'Krankheit';
      case 'symptom':
        return 'Symptom';
      case 'consultation':
      default:
        return 'Beratung';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const handleBlogPostClick = (postId: string) => {
    const post = blogPosts.find(p => p.id === postId);
    if (post) {
      const slug = createUniqueSlug(post.title, post.id);
      // Pass topic ID as state for back navigation if we're showing related articles
      const navigationState = showAsRelatedArticles && topicId ? { fromTopicId: topicId } : {};
      navigate(`/wissen/${slug}`, { state: navigationState });
    }
  };

  const handleTopicClick = (topic: any) => {
    const slug = createUniqueSlug(topic.name, topic.id);
    const topicType = topic.topic_type || 'consultation';
    
    // Pass blog post ID as state for back navigation
    const navigationState = blogPostId ? { fromBlogPostId: blogPostId } : {};
    
    switch (topicType) {
      case 'disease':
        navigate(`/krankheiten/${slug}`, { state: navigationState });
        break;
      case 'symptom':
        navigate(`/symptome/${slug}`, { state: navigationState });
        break;
      case 'consultation':
      default:
        navigate(`/beratung/${slug}`, { state: navigationState });
        break;
    }
  };

  if (loading || (showAsRelatedArticles && blogLoading)) {
    return (
      <div className={`${className}`}>
        <div className="animate-pulse flex flex-wrap gap-2">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-6 bg-gray-200 rounded-full w-20"></div>
          ))}
        </div>
      </div>
    );
  }

  // Show related blog articles if requested
  if (showAsRelatedArticles) {
    if (relatedBlogPosts.length === 0) {
      return null;
    }

    return (
      <div className={`mb-12 ${className}`}>
        <h3 className="text-2xl font-bold text-gray-900 mb-8 flex items-center">
          <BookOpen className="w-6 h-6 mr-3 text-blue-600" />
          Verwandte Artikel
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {relatedBlogPosts.slice(0, 6).map((post) => (
            <BlogCard
              key={post.id}
              post={post}
              onClick={handleBlogPostClick}
              className="w-full bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow cursor-pointer border border-gray-200 overflow-hidden"
              imageHeight="large"
            />
          ))}
        </div>
        
        {relatedBlogPosts.length > 6 && (
          <div className="text-center mt-6">
            <button
              onClick={() => navigate('/wissen')}
              className="text-blue-600 hover:text-blue-800 font-medium"
            >
              Alle {relatedBlogPosts.length} verwandten Artikel anzeigen →
            </button>
          </div>
        )}
      </div>
    );
  }

  // Show topic tags for blog posts (original functionality)
  if (!activeTopicTags || activeTopicTags.length === 0) {
    return null;
  }

  return (
    <div className={`bg-gray-50 rounded-xl p-6 my-8 ${className}`}>
      <div className="flex items-center mb-4">
        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
          <Tag className="w-4 h-4 text-blue-600" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900">
          Verwandte Gesundheitsthemen
        </h3>
      </div>
      <p className="text-gray-600 mb-4">
        Erfahren Sie mehr über die in diesem Artikel behandelten Themen:
      </p>
      
      <div className="flex flex-wrap gap-3">
        {activeTopicTags.map((blogTopicTag) => {
          const topic = blogTopicTag.topic;
          const topicType = topic.topic_type || 'consultation';
          
          // Get appropriate icon for topic type
          const getTopicIcon = () => {
            switch (topicType) {
              case 'disease':
                return Activity; // Medical activity icon for diseases
              case 'symptom':
                return AlertTriangle; // Warning triangle for symptoms
              case 'consultation':
              default:
                return Heart; // Heart icon for consultation/care
            }
          };
          
          const TopicIcon = getTopicIcon();
          
          return (
            <button
              key={blogTopicTag.id}
              onClick={() => handleTopicClick(topic)}
              className={`inline-flex items-center px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 hover:scale-105 hover:shadow-md ${
                topicType === 'disease' 
                  ? 'bg-red-100 text-red-800 hover:bg-red-200 border border-red-200'
                  : topicType === 'symptom'
                  ? 'bg-amber-100 text-amber-800 hover:bg-amber-200 border border-amber-200'
                  : 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200 border border-emerald-200'
              }`}
            >
              <TopicIcon className="w-4 h-4 mr-2" />
              {topic.name}
              <ArrowRight className="w-4 h-4 ml-2 opacity-60 group-hover:opacity-100 transition-opacity" />
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default BlogTopicTagsDisplay;